import React, { useState, useRef, useEffect } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { supabase } from '../lib/supabase';
import { useBusinessId } from '../hooks/use-business-id';
import { Phone, PhoneCall, PhoneOff, X, Delete, PhoneMissed, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { CallHistoryService, CallHistoryItem } from '../services/callHistoryService';

export default function CallsBeta() {
  const businessId = useBusinessId();
  const deviceRef = useRef<Device | null>(null);
  
  // Device state
  const [deviceStatus, setDeviceStatus] = useState<'initializing' | 'ready' | 'error'>('initializing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Call state
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [callStatus, setCallStatus] = useState<'idle' | 'connecting' | 'connected'>('idle');
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'dialer' | 'history'>('dialer');
  
  // Real call history data
  const [callHistory, setCallHistory] = useState<CallHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [unviewedMissedCount, setUnviewedMissedCount] = useState(0);

  // Function to count unviewed missed calls
  const countUnviewedMissedCalls = async () => {
    if (!businessId) return;
    
    try {
      const { count, error } = await supabase
        .from('call_history')
        .select('*', { count: 'exact', head: true })
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (error) {
        console.error('Error counting unviewed missed calls:', error);
      } else {
        const missedCount = count || 0;
        setUnviewedMissedCount(missedCount);
        console.log(`📞 Badge count: ${missedCount} unviewed missed calls`);
      }
    } catch (error) {
      console.error('Error in countUnviewedMissedCalls:', error);
    }
  };
  
  // Customer lookup for typed number
  const [matchedCustomer, setMatchedCustomer] = useState<{first_name: string, last_name: string} | null>(null);
  
  // Add digit to phone number
  const addDigit = (digit: string) => {
    if (callStatus === 'idle') {
      setPhoneNumber(prev => prev + digit);
    }
  };
  
  // Remove last digit
  const removeDigit = () => {
    if (callStatus === 'idle') {
      setPhoneNumber(prev => prev.slice(0, -1));
    }
  };

  // Fetch call history
  const fetchCallHistory = async () => {
    if (!businessId) return;
    
    setLoadingHistory(true);
    try {
      const { data, error } = await CallHistoryService.getCallHistory(businessId);
      
      if (error) {
        console.error('Error fetching call history:', error);
      } else {
        setCallHistory(data || []);
        
        // Count unviewed missed inbound calls
        const unviewedCount = (data || []).filter(call => 
          call.direction === 'inbound' && 
          call.status === 'missed' && 
          call.viewed === false
        ).length;
        
        setUnviewedMissedCount(unviewedCount);
        console.log(`📞 Unviewed missed calls: ${unviewedCount}`);
        console.log('📞 Call history debug:', (data || []).map(call => ({
          id: call.id,
          direction: call.direction,
          status: call.status,
          viewed: call.viewed
        })));
      }
    } catch (err) {
      console.error('Error in fetchCallHistory:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Lookup customer by phone number as user types
  const lookupCustomerByNumber = async (number: string) => {
    if (!businessId || !number || number.length < 3) {
      setMatchedCustomer(null);
      return;
    }

    try {
      // Normalize the typed number
      const normalizedNumber = number.replace(/\D/g, '');
      
      // Try different formats for matching
      const searchPatterns = [
        number, // Exact as typed
        normalizedNumber, // Just digits
        `+1${normalizedNumber}`, // With +1
        `+${normalizedNumber}`, // With +
        `1${normalizedNumber}` // With 1
      ];

      console.log('🔍 Looking up customer for patterns:', searchPatterns);

      const { data: customers, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name, phone')
        .eq('business_id', businessId);

      if (error || !customers) {
        console.error('Error fetching customers for lookup:', error);
        setMatchedCustomer(null);
        return;
      }

      // Find matching customer by normalizing their phone numbers
      const matchedCustomer = customers.find(customer => {
        if (!customer.phone) return false;
        
        const customerNormalized = customer.phone.replace(/\D/g, '');
        
        // Check if any search pattern matches the normalized customer phone
        return searchPatterns.some(pattern => {
          const patternNormalized = pattern.replace(/\D/g, '');
          return patternNormalized === customerNormalized ||
                 patternNormalized === customerNormalized.substring(1) || // Remove country code
                 `1${patternNormalized}` === customerNormalized; // Add country code
        });
      });

      if (matchedCustomer) {
        console.log('✅ Found matching customer:', matchedCustomer.first_name, matchedCustomer.last_name);
        setMatchedCustomer({
          first_name: matchedCustomer.first_name,
          last_name: matchedCustomer.last_name
        });
      } else {
        setMatchedCustomer(null);
      }

    } catch (err) {
      console.error('Error in customer lookup:', err);
      setMatchedCustomer(null);
    }
  };

  // Count unviewed missed calls on component mount and business change
  useEffect(() => {
    if (businessId) {
      countUnviewedMissedCalls();
    }
  }, [businessId]);

  // Fetch call history when tab changes to history or component mounts
  useEffect(() => {
    if (activeTab === 'history' && businessId) {
      fetchCallHistory();
      // Auto-mark unviewed missed calls as viewed when navigating to recents tab
      markMissedCallsAsViewed();
    }
  }, [activeTab, businessId]);

  // Function to mark all unviewed missed calls as viewed
  const markMissedCallsAsViewed = async () => {
    if (!businessId) return;
    
    try {
      console.log('👀 Marking unviewed missed calls as viewed...');
      
      const { error } = await supabase
        .from('call_history')
        .update({ viewed: true })
        .eq('business_id', businessId)
        .eq('direction', 'inbound')
        .eq('status', 'missed')
        .eq('viewed', false);

      if (error) {
        console.error('Error marking missed calls as viewed:', error);
      } else {
        console.log('✅ Marked unviewed missed calls as viewed');
        // Reset the unviewed count immediately for instant UI feedback
        setUnviewedMissedCount(0);
        
        // Silent update - update call history state without loading indicator
        setCallHistory(prevHistory => 
          prevHistory.map(call => 
            call.direction === 'inbound' && call.status === 'missed' && call.viewed === false
              ? { ...call, viewed: true }
              : call
          )
        );
        
        // Also trigger a recount to ensure consistency
        setTimeout(() => countUnviewedMissedCalls(), 100);
      }
    } catch (error) {
      console.error('Error in markMissedCallsAsViewed:', error);
    }
  };

  // Lookup customer when phone number changes
  useEffect(() => {
    if (activeTab === 'dialer' && phoneNumber.length >= 3) {
      lookupCustomerByNumber(phoneNumber);
    } else {
      setMatchedCustomer(null);
    }
  }, [phoneNumber, activeTab, businessId]);

  // Set up real-time subscription for call history
  useEffect(() => {
    if (!businessId) return;

    console.log('🔄 Setting up real-time call history subscription for business:', businessId);
    
    const channel = supabase
      .channel(`call_history_${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${businessId}`
        },
        (payload) => {
          console.log('📞 Real-time call history update:', payload);
          
          // Refresh call history when changes occur
          if (activeTab === 'history') {
            fetchCallHistory();
          }
          
          // Always recount unviewed missed calls for badge updates
          countUnviewedMissedCalls();
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 Cleaning up real-time call history subscription');
      supabase.removeChannel(channel);
    };
  }, [businessId, activeTab]);

  // Initialize device on component mount
  useEffect(() => {
    let isMounted = true;
    
    const initializeDevice = async () => {
      try {
        if (!businessId) {
          if (isMounted) {
            setErrorMessage('Business ID not found');
            setDeviceStatus('error');
          }
          return;
        }

        if (isMounted) {
          setDeviceStatus('initializing');
          setErrorMessage('');
        }

        // Get access token
        const { data: tokenResponse, error: tokenError } = await supabase.functions.invoke('twilio-access-token', {
          body: { business_id: businessId }
        });

        if (!isMounted) return; // Component unmounted during async operation

        if (tokenError || !tokenResponse?.success) {
          throw new Error(`Failed to get access token: ${tokenError?.message || tokenResponse?.error}`);
        }

        const { access_token } = tokenResponse;

        // Create Twilio Device
        const device = new Device(access_token, {
          logLevel: 0, // Minimal logging
          codecPreferences: ['opus', 'pcmu'],
          allowIncomingWhileBusy: true
        });

        if (!isMounted) return; // Component unmounted during device creation

        deviceRef.current = device;

        // Set up device event listeners
        device.on('registered', () => {
          if (isMounted) {
            setDeviceStatus('ready');
            setErrorMessage('');
          }
        });

        device.on('unregistered', () => {
          if (isMounted) {
            setDeviceStatus('error');
            setErrorMessage('Device disconnected');
          }
        });

        device.on('error', (error) => {
          if (isMounted) {
            setDeviceStatus('error');
            setErrorMessage(`Device error: ${error.message}`);
          }
        });

        device.on('destroy', () => {
          if (isMounted) {
            setDeviceStatus('error');
            setErrorMessage('Device destroyed');
          }
        });

        // Call event listeners
        device.on('connect', (call) => {
          if (isMounted) {
            setCurrentCall(call);
            setCallStatus('connected');
          }
        });

        device.on('disconnect', (call) => {
          if (isMounted) {
            setCurrentCall(null);
            setCallStatus('idle');
          }
        });

        device.on('incoming', (call) => {
          if (isMounted) {
            setCurrentCall(call);
            setCallStatus('connecting');
          }
          
          // Set up incoming call event listeners
          call.on('accept', () => {
            if (isMounted) {
              setCallStatus('connected');
            }
          });
          
          call.on('disconnect', () => {
            if (isMounted) {
              setCurrentCall(null);
              setCallStatus('idle');
            }
          });
          
          // Auto-accept incoming calls
          call.accept();
        });

        // Register the device
        device.register();

      } catch (error) {
        console.error('CallsBeta initialization error:', error);
        if (isMounted) {
          setDeviceStatus('error');
          setErrorMessage(error?.message || 'Initialization failed');
        }
      }
    };

    initializeDevice().catch(error => {
      console.error('CallsBeta async initialization error:', error);
      if (isMounted) {
        setDeviceStatus('error');
        setErrorMessage('Failed to initialize calling');
      }
    });

    return () => {
      isMounted = false;
    };
  }, [businessId]);

  // Make a call
  const makeCall = async () => {
    if (!deviceRef.current || !phoneNumber || deviceStatus !== 'ready') return;
    
    try {
      // Request microphone access
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const formattedNumber = formatPhoneNumber(phoneNumber);
      setCallStatus('connecting');
      
      const call = await deviceRef.current.connect({
        params: { To: formattedNumber }
      });
      
      setCurrentCall(call);
      
      // Set up call-specific event listeners
      call.on('accept', () => {
        setCallStatus('connected');
      });
      
      call.on('disconnect', () => {
        setCurrentCall(null);
        setCallStatus('idle');
      });
      
      call.on('reject', () => {
        setCurrentCall(null);
        setCallStatus('idle');
      });
      
      call.on('cancel', () => {
        setCurrentCall(null);
        setCallStatus('idle');
      });
      
    } catch (error) {
      setCallStatus('idle');
      setCurrentCall(null);
      setErrorMessage(`Call failed: ${error.message}`);
    }
  };

  // End current call
  const endCall = () => {
    if (currentCall) {
      currentCall.disconnect();
      setCurrentCall(null);
      setCallStatus('idle');
    }
  };

  // Format phone number as E.164
  const formatPhoneNumber = (number: string) => {
    // Remove all non-digits
    const digits = number.replace(/\D/g, '');
    
    // Add +1 if it's a 10-digit US number
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // Add + if it doesn't start with +
    if (digits.length > 10 && !number.startsWith('+')) {
      return `+${digits}`;
    }
    
    return number;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        console.log('🧹 Cleaning up device on unmount');
        deviceRef.current.destroy();
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 top-12 bg-gray-100 flex flex-col justify-center p-4 gap-6 overflow-hidden">
      {/* Phone Number Display Card - Only show on dialer tab */}
      {activeTab === 'dialer' && (
        <div className="bg-white rounded-xl shadow-sm p-2 flex-shrink-0 relative">
        {/* Status Indicator - Top Right Corner */}
        <div className="absolute top-2 right-2">
          <div className={`w-3 h-3 rounded-full ${
            deviceStatus === 'ready' ? 'bg-green-500' :
            deviceStatus === 'initializing' ? 'bg-yellow-500' :
            'bg-red-500'
          }`}></div>
        </div>

        <div className="text-center">
          {/* Show error message only if there's an error */}
          {errorMessage && deviceStatus === 'error' && (
            <div className="mb-2">
              <p className="text-red-600 text-xs">{errorMessage}</p>
            </div>
          )}

          {/* Phone Number Display - Compact */}
          {callStatus !== 'idle' ? (
            <div>
              <div className={`text-xs mb-1 font-medium ${
                callStatus === 'connecting' ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {callStatus === 'connecting' ? 'Calling...' : 'Connected'}
              </div>
              <div className="text-gray-900 text-xl font-light">
                {formatPhoneNumber(phoneNumber)}
              </div>
            </div>
          ) : (
            <div className="text-center h-16 flex flex-col items-center justify-center">
              {phoneNumber ? (
                <div>
                  <div className="text-gray-900 text-2xl font-light tracking-wider">
                    {phoneNumber}
                  </div>
                  {matchedCustomer && (
                    <div className="text-blue-600 text-sm font-medium mt-1">
                      {`${matchedCustomer.first_name} ${matchedCustomer.last_name}`.trim()}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 text-lg">Enter number</span>
              )}
            </div>
          )}
        </div>
        </div>
      )}

      {/* Dialer Card - Show when dialer tab is active */}
      {callStatus === 'idle' && activeTab === 'dialer' && (
        <div className="bg-white rounded-xl shadow-sm p-4 pt-6 pb-2 mt-2 mb-16">
          <div className="grid grid-cols-3 gap-4 justify-items-center mb-4">
            {/* Row 1 */}
            <button onClick={() => addDigit('1')} className="keypad-button">
              <span className="text-3xl font-light">1</span>
            </button>
            <button onClick={() => addDigit('2')} className="keypad-button">
              <span className="text-3xl font-light">2</span>
            </button>
            <button onClick={() => addDigit('3')} className="keypad-button">
              <span className="text-3xl font-light">3</span>
            </button>

            {/* Row 2 */}
            <button onClick={() => addDigit('4')} className="keypad-button">
              <span className="text-3xl font-light">4</span>
            </button>
            <button onClick={() => addDigit('5')} className="keypad-button">
              <span className="text-3xl font-light">5</span>
            </button>
            <button onClick={() => addDigit('6')} className="keypad-button">
              <span className="text-3xl font-light">6</span>
            </button>

            {/* Row 3 */}
            <button onClick={() => addDigit('7')} className="keypad-button">
              <span className="text-3xl font-light">7</span>
            </button>
            <button onClick={() => addDigit('8')} className="keypad-button">
              <span className="text-3xl font-light">8</span>
            </button>
            <button onClick={() => addDigit('9')} className="keypad-button">
              <span className="text-3xl font-light">9</span>
            </button>

            {/* Row 4 */}
            <button onClick={() => addDigit('*')} className="keypad-button">
              <span className="text-3xl font-light">*</span>
            </button>
            <button onClick={() => addDigit('0')} className="keypad-button">
              <span className="text-3xl font-light">0</span>
            </button>
            <button onClick={() => addDigit('#')} className="keypad-button">
              <span className="text-3xl font-light">#</span>
            </button>
          </div>

          {/* Bottom Controls */}
          <div className="flex items-center justify-center gap-8">
            {/* Delete Button */}
            <button
              onClick={removeDigit}
              disabled={!phoneNumber}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                phoneNumber 
                  ? 'text-gray-700 hover:bg-gray-200' 
                  : 'text-gray-400'
              }`}
            >
              <Delete className="w-5 h-5" />
            </button>

            {/* Call Button */}
            <button
              onClick={makeCall}
              disabled={!phoneNumber.trim()}
              className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${
                phoneNumber.trim()
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg'
                  : 'bg-gray-300 text-gray-500'
              }`}
            >
              <Phone className="w-6 h-6" />
            </button>

            {/* Placeholder for symmetry */}
            <div className="w-12 h-12"></div>
          </div>
        </div>
      )}

      {/* Call History Card - Show when history tab is active */}
      {callStatus === 'idle' && activeTab === 'history' && (
        <div className="bg-white rounded-xl shadow-sm p-4 mt-2 flex-1 overflow-hidden mb-16">
          <div className="h-full overflow-y-auto">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full"></div>
                <span className="ml-2 text-gray-600">Loading history...</span>
              </div>
            ) : callHistory.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-gray-500">No call history yet</p>
              </div>
            ) : (
              callHistory.map((call) => {
                const callType = CallHistoryService.getCallType(call.direction, call.status);
                const displayName = CallHistoryService.getDisplayName(call);
                const displaySubtitle = CallHistoryService.getDisplaySubtitle(call);
                const formattedTime = CallHistoryService.formatCallTime(call.call_time);
                const formattedDuration = CallHistoryService.formatDuration(call.duration_seconds);
                
                return (
                  <div key={call.id} className="flex items-center py-3 border-b border-gray-100 last:border-b-0 relative">
                    {/* Call Type Icon */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3">
                      {callType === 'missed' ? (
                        <PhoneMissed className="w-4 h-4 text-red-500" />
                      ) : callType === 'incoming' ? (
                        <PhoneIncoming className="w-4 h-4 text-green-500" />
                      ) : (
                        <PhoneOutgoing className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    
                    {/* Unviewed Missed Call Indicator */}
                    {callType === 'missed' && call.direction === 'inbound' && call.viewed === false && (
                      <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></div>
                    )}
                    
                    {/* Call Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                          <p className="text-xs text-gray-500 mt-1">{displaySubtitle}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{formattedTime}</p>
                          {formattedDuration && (
                            <p className="text-xs text-gray-400">{formattedDuration}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Call Back Button */}
                    <button
                      onClick={() => {
                        const numberToCall = call.direction === 'inbound' ? call.from_number : call.to_number;
                        setPhoneNumber(numberToCall.replace(/\D/g, ''));
                        setActiveTab('dialer');
                      }}
                      className="ml-3 w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center text-white transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Call Controls (when in call) - Minimal height */}
      {callStatus !== 'idle' && (
        <div className="bg-white rounded-2xl shadow-sm p-6 flex-shrink-0">
          <div className="flex justify-center">
            <button
              onClick={endCall}
              className="w-16 h-16 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg transition-all"
            >
              <PhoneOff className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation Card - Fixed at bottom */}
      {callStatus === 'idle' && (
        <div className="absolute bottom-4 left-4 right-4 bg-white rounded-xl shadow-sm p-1">
          <div className="flex">
            <button
              onClick={() => setActiveTab('dialer')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'dialer'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Keypad
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                activeTab === 'history'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {/* Badge count for unviewed missed calls - LEFT SIDE */}
              {unviewedMissedCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-semibold px-1.5 py-0.5 rounded-full min-w-[18px] h-[18px] flex items-center justify-center">
                  {unviewedMissedCount}
                </span>
              )}
              <span>Recents</span>
            </button>
          </div>
        </div>
      )}


      <style jsx>{`
        .keypad-button {
          width: 75px;
          height: 75px;
          background-color: #e5e7eb;
          border-radius: 50%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #374151;
          transition: all 0.15s ease;
          border: none;
          outline: none;
          margin: 0 auto;
        }
        
        .keypad-button:hover {
          background-color: #d1d5db;
        }
        
        .keypad-button:active {
          background-color: #9ca3af;
          transform: scale(0.95);
        }
        
        .keypad-button span:first-child {
          font-size: 600px;
          line-height: 1;
          font-weight: 700;
        }
        
        .keypad-button span:last-child {
          font-size: 10px;
          line-height: 1;
          margin-top: 2px;
          color: #6b7280;
        }
      `}</style>
    </div>
  );
}